/**
 * Navigation Prevention Utilities
 * 
 * This module provides comprehensive protection against accidental browser navigation
 * caused by trackpad swipe gestures, preventing users from losing their work.
 */

import { createFileLogger } from './logger';

const log = createFileLogger('NAVIGATION_PREVENTION');

interface NavigationPreventionOptions {
  enableGestureBlocking?: boolean;
  enableBeforeUnloadWarning?: boolean;
  enableKeyboardShortcuts?: boolean;
  customWarningMessage?: string;
}

class NavigationPrevention {
  private isEnabled = false;
  private hasUnsavedChanges = false;
  private options: NavigationPreventionOptions;
  private boundHandlers: { [key: string]: EventListener } = {};

  constructor(options: NavigationPreventionOptions = {}) {
    this.options = {
      enableGestureBlocking: true,
      enableBeforeUnloadWarning: true,
      enableKeyboardShortcuts: true,
      customWarningMessage: 'You have unsaved changes that will be lost if you leave this page.',
      ...options
    };
  }

  /**
   * Enable navigation prevention
   */
  enable(): void {
    if (this.isEnabled) return;
    
    log.log('[NavigationPrevention] Enabling navigation prevention');
    this.isEnabled = true;

    if (this.options.enableGestureBlocking) {
      this.enableGestureBlocking();
    }

    if (this.options.enableBeforeUnloadWarning) {
      this.enableBeforeUnloadWarning();
    }

    if (this.options.enableKeyboardShortcuts) {
      this.enableKeyboardShortcuts();
    }
  }

  /**
   * Disable navigation prevention
   */
  disable(): void {
    if (!this.isEnabled) return;

    log.log('[NavigationPrevention] Disabling navigation prevention');
    this.isEnabled = false;

    // Remove all event listeners
    Object.entries(this.boundHandlers).forEach(([event, handler]) => {
      const [target, eventName] = event.split(':');
      if (target === 'window') {
        window.removeEventListener(eventName, handler);
      } else if (target === 'document') {
        document.removeEventListener(eventName, handler);
      }
    });

    this.boundHandlers = {};
  }

  /**
   * Set whether there are unsaved changes
   */
  setHasUnsavedChanges(hasChanges: boolean): void {
    this.hasUnsavedChanges = hasChanges;
    log.log(`[NavigationPrevention] Unsaved changes: ${hasChanges}`);
  }

  /**
   * Block trackpad and mouse gesture navigation
   */
  private enableGestureBlocking(): void {
    // Block horizontal wheel events that could trigger navigation
    const wheelHandler = (event: Event) => {
      const e = event as WheelEvent;
      // Check if this is a horizontal scroll that could trigger navigation
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY) && Math.abs(e.deltaX) > 50) {
        // Only prevent if it's at the edge of scrollable content
        const target = e.target as Element;
        const scrollableParent = this.findScrollableParent(target);
        
        if (scrollableParent) {
          const { scrollLeft, scrollWidth, clientWidth } = scrollableParent;
          const atLeftEdge = scrollLeft <= 0;
          const atRightEdge = scrollLeft >= scrollWidth - clientWidth;
          
          // Prevent navigation if we're at the edge and trying to scroll further
          if ((e.deltaX > 0 && atRightEdge) || (e.deltaX < 0 && atLeftEdge)) {
            e.preventDefault();
            e.stopPropagation();
            log.log('[NavigationPrevention] Blocked horizontal wheel navigation gesture');
          }
        } else {
          // No scrollable parent, prevent navigation
          e.preventDefault();
          e.stopPropagation();
          log.log('[NavigationPrevention] Blocked horizontal wheel navigation gesture (no scrollable parent)');
        }
      }
    };

    // Block touch gestures that could trigger navigation
    let startX = 0;
    let startY = 0;
    
    const touchStartHandler = (event: Event) => {
      const e = event as TouchEvent;
      if (e.touches.length === 2) {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
      }
    };

    const touchMoveHandler = (event: Event) => {
      const e = event as TouchEvent;
      if (e.touches.length === 2) {
        const deltaX = e.touches[0].clientX - startX;
        const deltaY = e.touches[0].clientY - startY;
        
        // If it's primarily a horizontal gesture
        if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
          const target = e.target as Element;
          const scrollableParent = this.findScrollableParent(target);
          
          if (scrollableParent) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollableParent;
            const atLeftEdge = scrollLeft <= 0;
            const atRightEdge = scrollLeft >= scrollWidth - clientWidth;
            
            if ((deltaX > 0 && atLeftEdge) || (deltaX < 0 && atRightEdge)) {
              e.preventDefault();
              e.stopPropagation();
              log.log('[NavigationPrevention] Blocked touch navigation gesture');
            }
          } else {
            e.preventDefault();
            e.stopPropagation();
            log.log('[NavigationPrevention] Blocked touch navigation gesture (no scrollable parent)');
          }
        }
      }
    };

    // Add event listeners
    this.addEventHandler('window:wheel', wheelHandler, { passive: false });
    this.addEventHandler('document:touchstart', touchStartHandler, { passive: false });
    this.addEventHandler('document:touchmove', touchMoveHandler, { passive: false });
  }

  /**
   * Enable beforeunload warning for unsaved changes
   */
  private enableBeforeUnloadWarning(): void {
    const beforeUnloadHandler = (event: Event) => {
      const e = event as BeforeUnloadEvent;
      if (this.hasUnsavedChanges) {
        const message = this.options.customWarningMessage || 'You have unsaved changes that will be lost if you leave this page.';
        e.preventDefault();
        e.returnValue = message;
        log.log('[NavigationPrevention] Showing beforeunload warning');
        return message;
      }
    };

    // Also handle page visibility changes
    const visibilityChangeHandler = () => {
      if (document.hidden && this.hasUnsavedChanges) {
        log.log('[NavigationPrevention] Page hidden with unsaved changes');
      }
    };

    this.addEventHandler('window:beforeunload', beforeUnloadHandler);
    this.addEventHandler('document:visibilitychange', visibilityChangeHandler);
  }

  /**
   * Block keyboard shortcuts that could trigger navigation
   */
  private enableKeyboardShortcuts(): void {
    const keyHandler = (event: Event) => {
      const e = event as KeyboardEvent;
      // Block Alt+Left/Right (browser back/forward)
      if (e.altKey && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
        if (this.hasUnsavedChanges) {
          e.preventDefault();
          e.stopPropagation();
          log.log('[NavigationPrevention] Blocked Alt+Arrow navigation shortcut');
        }
      }
      
      // Block Cmd+Left/Right on macOS (browser back/forward)
      if (e.metaKey && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
        if (this.hasUnsavedChanges) {
          e.preventDefault();
          e.stopPropagation();
          log.log('[NavigationPrevention] Blocked Cmd+Arrow navigation shortcut');
        }
      }

      // Block Backspace navigation (when not in input)
      if (e.key === 'Backspace') {
        const target = e.target as Element;
        const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || 
                       target.hasAttribute('contenteditable');
        
        if (!isInput && this.hasUnsavedChanges) {
          e.preventDefault();
          e.stopPropagation();
          log.log('[NavigationPrevention] Blocked Backspace navigation');
        }
      }
    };

    this.addEventHandler('document:keydown', keyHandler, { capture: true });
  }

  /**
   * Find the nearest scrollable parent element
   */
  private findScrollableParent(element: Element): Element | null {
    let parent = element.parentElement;
    
    while (parent) {
      const style = window.getComputedStyle(parent);
      const overflowX = style.overflowX;
      
      if (overflowX === 'scroll' || overflowX === 'auto') {
        return parent;
      }
      
      parent = parent.parentElement;
    }
    
    return null;
  }

  /**
   * Add event handler and store reference for cleanup
   */
  private addEventHandler(
    key: string, 
    handler: (event: Event) => void, 
    options?: AddEventListenerOptions
  ): void {
    const [target, eventName] = key.split(':');
    
    if (target === 'window') {
      window.addEventListener(eventName, handler, options);
    } else if (target === 'document') {
      document.addEventListener(eventName, handler, options);
    }
    
    this.boundHandlers[key] = handler as EventListener;
  }
}

// Create singleton instance
let navigationPreventionInstance: NavigationPrevention | null = null;

/**
 * Get the singleton navigation prevention instance
 */
export function getNavigationPrevention(options?: NavigationPreventionOptions): NavigationPrevention {
  if (!navigationPreventionInstance) {
    navigationPreventionInstance = new NavigationPrevention(options);
  }
  return navigationPreventionInstance;
}

/**
 * Convenience function to enable navigation prevention globally
 */
export function enableNavigationPrevention(options?: NavigationPreventionOptions): NavigationPrevention {
  const instance = getNavigationPrevention(options);
  instance.enable();
  return instance;
}

/**
 * Convenience function to disable navigation prevention globally
 */
export function disableNavigationPrevention(): void {
  if (navigationPreventionInstance) {
    navigationPreventionInstance.disable();
  }
}

/**
 * Convenience function to set unsaved changes state
 */
export function setHasUnsavedChanges(hasChanges: boolean): void {
  if (navigationPreventionInstance) {
    navigationPreventionInstance.setHasUnsavedChanges(hasChanges);
  }
}
